import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, Mail, Calendar, AlertTriangle, ChevronRight, ChevronLeft } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import CustomerNavbar from '../components/CustomerNavbar';
import Footer from '../components/Footer';
import { sendBusinessFunnelEvent } from '../analytics';

const RegistrationSuccessPage: React.FC = () => {
  const { user, logout } = useAuth();
  const { loadUserBusinesses } = useBusiness();
  const navigate = useNavigate();
  const location = useLocation();

  // Get redirect parameter, account type, and whether this was adding a business to an existing account
  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get('redirect');
  const accountTypeParam = searchParams.get('type');
  const accountType = accountTypeParam || 'business'; // Default to business for backward compatibility
  const isExistingAccount = searchParams.get('existingAccount') === '1'; // Added business to existing customer account

  useEffect(() => {
    if (accountTypeParam === 'business') {
      sendBusinessFunnelEvent('business_registration_complete');
    }
  }, [accountTypeParam]);

  const handlePrimaryAction = async () => {
    if (accountType === 'business' && isExistingAccount && user) {
      await loadUserBusinesses();
      const dashboardUrl = redirectUrl
        ? `/dashboard/business?redirect=${encodeURIComponent(redirectUrl)}`
        : '/dashboard/business';
      navigate(dashboardUrl);
      return;
    }
    const params = new URLSearchParams();
    if (accountType === 'business') params.set('type', 'business');
    if (redirectUrl) params.set('redirect', redirectUrl);
    const queryString = params.toString();
    navigate(queryString ? `/login?${queryString}` : '/login');
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-white no-animations flex flex-col">
      {/* Customer Navbar */}
      <CustomerNavbar 
        onLogout={handleLogout}
        showDashboardLink={false}
        showLogoutButton={false}
        showAuthButtons={true}
      />
      
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      
      <div className="max-w-md w-full space-y-8 relative z-10">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-green-600" strokeWidth={2} />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">
            Congratulations!
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {accountType === 'business' && isExistingAccount
              ? 'Your business has been added to your account successfully.'
              : `Your ${accountType === 'customer' ? 'customer' : 'business'} account has been created successfully`}
          </p>
        </div>

        {/* Success Card */}
        <div className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm">
          {/* Success Message */}
          <div className="text-center mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {accountType === 'business' && isExistingAccount
                ? "You're all set!"
                : 'Welcome to AACore Booking Platform!'}
            </h3>
            <p className="text-gray-600">
              {accountType === 'business' && isExistingAccount
                ? "Your account was already verified. You're now ready to sign in and finish setting up your business."
                : `Your ${accountType === 'customer' ? 'customer' : 'business'} account has been successfully created. You're just one step away from getting started.`}
            </p>
          </div>

          {/* Email Verification Section - only for new accounts, not when adding business to existing account */}
          {!(accountType === 'business' && isExistingAccount) && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <div className="flex items-start space-x-3">
              <Mail className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-blue-900 mb-2">
                  Email Verification Required
                </h4>
                <p className="text-sm text-blue-700 mb-3">
                  We've sent a verification link to your email address. Please check your inbox and click the verification link to activate your account.
                </p>
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" aria-hidden />
                  <span className="text-xs font-bold uppercase tracking-wide text-amber-800">IMPORTANT</span>
                </div>
                <ul className="space-y-2 text-sm font-semibold text-blue-900">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-0.5 flex-shrink-0">•</span>
                    <span>If you don&apos;t see the code within 2–3 minutes, please check your spam box.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-0.5 flex-shrink-0">•</span>
                    <span>The verification link will expire in 24 hours.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-0.5 flex-shrink-0">•</span>
                    <span>You can request a new verification email from the login page.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          )}

          {/* Next Steps */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
            <div className="flex items-start space-x-3">
              <Calendar className="h-5 w-5 text-gray-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  What&apos;s Next?
                </h4>
                {accountType === 'business' && isExistingAccount ? (
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Sign in to your account</li>
                    <li>• Add your services</li>
                    <li>• Set your hours of operation</li>
                    <li>• Add staff and start accepting bookings</li>
                  </ul>
                ) : (
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Verify your email address</li>
                  <li>• Log in to your account</li>
                  {accountType === 'customer' ? (
                    <>
                      <li>• Browse local businesses</li>
                      <li>• Book appointments easily</li>
                      <li>• Manage your bookings</li>
                    </>
                  ) : (
                    <>
                      <li>• Set up your business profile</li>
                      <li>• Add your services and staff</li>
                      <li>• Start accepting bookings!</li>
                    </>
                  )}
                </ul>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handlePrimaryAction}
              className="aacore-btn-primary w-full justify-center flex items-center gap-2"
            >
              {accountType === 'business' && isExistingAccount ? (
                <>
                  Go to business Dashboard
                  <ChevronRight className="h-5 w-5" aria-hidden />
                </>
              ) : (
                <>
                  Go to Login
                  <ChevronRight className="h-5 w-5" aria-hidden />
                </>
              )}
            </button>
            
            <Link
              to="/"
              className="aacore-btn-outlined w-full justify-center flex items-center gap-2"
            >
              <ChevronLeft className="h-5 w-5" aria-hidden />
              Back to Home
            </Link>
          </div>
        </div>

        {/* Help Section */}
        <div className="text-center">
          <p className="text-sm text-gray-600">
            Need help?{' '}
            <Link
              to="/contact"
              className="text-blue-600 hover:text-blue-700 underline"
            >
              Contact our support team
            </Link>
          </p>
        </div>
      </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default RegistrationSuccessPage; 